// src/layout/Topbar.jsx
//
// Redesigned app-shell topbar: sidebar-collapse toggle, Org/Project
// switchers, notifications, help, and the user menu. Manages which single
// dropdown is open — opening one closes the others, matching the approved
// design — and closes on outside click.
//
// OrgSwitcher/ProjectSwitcher (and the separator between them) only render
// once there's something to switch between — a user with zero visible
// Accounts, or whose current Account has zero Projects, doesn't see an
// empty/pointless switcher pill. They're also force-hidden via
// `hideSwitchers` on admin pages (Users/Roles/Permissions/Accounts) that
// aren't scoped to a single account/project — see Dashboard.jsx.
//
// Note: a PlanSwitcher (workspace entitlement) component still exists at
// ./PlanSwitcher.jsx but isn't rendered here — dropped per request since
// there's no real plan/entitlement concept yet. Re-add it here if/when
// module-subscription gating becomes real.

import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { PanelLeft, CircleHelp, ChevronRight } from "lucide-react";
import OrgSwitcher from "./OrgSwitcher";
import ProjectSwitcher from "./ProjectSwitcher";
import NotificationsMenu from "./NotificationsMenu";
import UserMenu from "./UserMenu";
import { useWorkspace } from "../context/WorkspaceContext";

export default function Topbar({
  onToggleSidebar,
  username,
  role,
  onLogout,
  scrolled = false,
  hideSwitchers = false,
}) {
  const [openMenu, setOpenMenu] = useState(null);
  const ref = useRef(null);
  const { loading, accounts, engagements } = useWorkspace();
  const showOrgSwitcher = !hideSwitchers && (loading || accounts.length > 0);
  const showProjectSwitcher = !hideSwitchers && (loading || engagements.length > 0);

  useEffect(() => {
    function onDocClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpenMenu(null);
    }
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  const toggle = (name) => setOpenMenu((m) => (m === name ? null : name));

  return (
    <header className={`orion-topbar${scrolled ? " scrolled" : ""}`} ref={ref}>
      <button type="button" className="orion-iconbtn orion-sq-btn" onClick={onToggleSidebar} title="Collapse sidebar">
        <PanelLeft size={18} />
      </button>

      {showOrgSwitcher && <OrgSwitcher open={openMenu === "org"} onToggle={() => toggle("org")} />}
      {showOrgSwitcher && showProjectSwitcher && (
        <span className="orion-sep-chev"><ChevronRight size={15} /></span>
      )}
      {showProjectSwitcher && <ProjectSwitcher open={openMenu === "proj"} onToggle={() => toggle("proj")} />}

      <div className="orion-header-spacer" />

      <NotificationsMenu open={openMenu === "notif"} onToggle={() => toggle("notif")} />

      <button type="button" className="orion-iconbtn orion-sq-btn-plain" title="Help">
        <CircleHelp size={18} />
      </button>

      <div className="orion-vdiv" />

      <UserMenu
        open={openMenu === "user"}
        onToggle={() => toggle("user")}
        username={username}
        role={role}
        onLogout={onLogout}
      />
    </header>
  );
}
Topbar.propTypes = {
  onToggleSidebar: PropTypes.func.isRequired,
  username: PropTypes.string.isRequired,
  role: PropTypes.string,
  onLogout: PropTypes.func.isRequired,
  scrolled: PropTypes.bool,
  hideSwitchers: PropTypes.bool,
};
